import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Store, Package, ShoppingBag, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { recordShare } from "@/lib/shareStats";
import { AuthImage } from "@/components/common/AuthImage";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const price = (cents) => (cents ? `$${(cents / 100).toFixed(2)}` : "Free");

// Sticker marketplace picker — pick an owned sticker to send, or browse & buy packs.
export function StickerPicker({ open, onOpenChange, onSend }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState("owned");
  const [buying, setBuying] = useState(null);
  const { data: owned, isLoading: loadingOwned } = useQuery({ queryKey: ["stickers-owned"], queryFn: api.stickersOwned, enabled: open && tab === "owned", retry: false });
  const { data: store, isLoading: loadingStore } = useQuery({ queryKey: ["sticker-packs", "store"], queryFn: () => api.stickerPacks("store"), enabled: open && tab === "store", retry: false });

  const ownedPacks = owned?.packs || [];
  const storePacks = store?.items || [];

  const refreshStickers = () => {
    qc.invalidateQueries({ queryKey: ["stickers-owned"] });
    qc.invalidateQueries({ queryKey: ["sticker-packs", "store"] });
  };

  const buy = async (packId) => {
    setBuying(packId);
    try {
      const res = await api.stickerPackPurchaseCheckout(packId, `${window.location.origin}/app/chats`);
      if (res?.granted) { toast.success("Added to your stickers!"); refreshStickers(); return; }
      if (res?.checkout_url) { window.location.href = res.checkout_url; return; }
      toast.error("Couldn't start checkout.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.error : null;
      if (code === "already_owned") { toast.info("You already own this pack."); refreshStickers(); }
      else if (code === "native_purchases_required" || e?.response?.status === 403) toast.info("Sticker packs are available in the BLOQ Talk app.");
      else if (e?.response?.status === 404) toast.info("The sticker store isn't live yet.");
      else toast.error((typeof detail === "object" ? detail?.message : detail) || "Couldn't purchase pack.");
    } finally {
      setBuying(null);
    }
  };

  const pick = (sticker) => { onSend?.(sticker); onOpenChange(false); };

  const sharePack = async (packId) => {
    const url = api.stickerShareUrl(packId);
    recordShare(packId);
    try { await navigator.clipboard.writeText(url); toast.success("Share link copied!"); }
    catch { toast.error("Couldn't copy the link."); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-bloq-s1 text-white sm:max-w-md" data-testid="sticker-picker">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><Store className="h-5 w-5 text-bloq-mint" /> Stickers</DialogTitle>
          <DialogDescription className="text-sm text-bloq-muted">Send a sticker, or grab a new pack from creators.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1.5">
          {[{ id: "owned", label: "My stickers", icon: Package }, { id: "store", label: "Store", icon: ShoppingBag }].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={cn("flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2 text-sm font-semibold transition-colors", tab === t.id ? "bg-bloq-mint/15 text-bloq-mint" : "text-bloq-muted hover:text-white")}
              data-testid={`sticker-tab-${t.id}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        <div className="max-h-80 overflow-y-auto">
          {tab === "owned" ? (
            loadingOwned ? (
              <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-bloq-mint" /></div>
            ) : ownedPacks.length === 0 ? (
              <p className="py-10 text-center text-sm text-bloq-muted" data-testid="stickers-empty">No sticker packs yet. Check out the Store tab.</p>
            ) : (
              <div className="space-y-4">
                {ownedPacks.map((pack) => (
                  <div key={pack.pack_id} data-testid={`owned-pack-${pack.pack_id}`}>
                    <div className="mb-1.5 text-xs font-semibold text-bloq-muted">{pack.title}</div>
                    <div className="grid grid-cols-4 gap-2">
                      {(pack.stickers || []).map((s) => (
                        <button key={s.id} onClick={() => pick(s)} className="aspect-square rounded-xl border border-white/8 bg-bloq-s2 p-1.5 transition-transform hover:scale-105" data-testid={`sticker-${s.id}`}>
                          <AuthImage src={api.stickerImageSrc(s.id)} alt={s.emoji || "sticker"} className="h-full w-full object-contain" />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : loadingStore ? (
            <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-bloq-mint" /></div>
          ) : storePacks.length === 0 ? (
            <p className="py-10 text-center text-sm text-bloq-muted" data-testid="store-empty">The sticker store is opening soon.</p>
          ) : (
            <div className="space-y-2">
              {storePacks.map((pack) => (
                <div key={pack.pack_id} className="flex items-center gap-3 rounded-xl border border-white/8 bg-bloq-s2 p-3" data-testid={`store-pack-${pack.pack_id}`}>
                  {pack.cover_url && <AuthImage src={api.stickerCoverSrc(pack.cover_url)} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" testid={`store-cover-${pack.pack_id}`} />}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold">{pack.title}</div>
                    <div className="text-[11px] text-bloq-muted">{pack.author?.handle ? `@${pack.author.handle}` : ""}{pack.sticker_count ? ` · ${pack.sticker_count} stickers` : ""}</div>
                  </div>
                  <button onClick={() => sharePack(pack.pack_id)} className="shrink-0 rounded-full p-2 text-bloq-muted hover:text-white" title="Copy share link" data-testid={`share-pack-${pack.pack_id}`}>
                    <Share2 className="h-4 w-4" />
                  </button>
                  {pack.owned ? (
                    <span className="text-xs font-semibold text-bloq-mint">Owned</span>
                  ) : (
                    <Button onClick={() => buy(pack.pack_id)} disabled={buying === pack.pack_id} size="sm" className="rounded-full bloq-gradient font-bold text-bloq-bg" data-testid={`buy-pack-${pack.pack_id}`}>
                      {buying === pack.pack_id ? <Loader2 className="h-4 w-4 animate-spin" /> : price(pack.price)}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
